var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import FormControlLabel from '@material-ui/core/FormControlLabel';
import classNames from 'classnames';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import * as yup from 'yup';
import { searchFrequency, searchRouteOfAdministration } from '../../Async/MedicationsSummaryAsync';
import { FormikDatePicker } from '../../Components/FormikFields/FormikDatePicker';
import { FormikStyledSelectAsync } from '../../Components/FormikFields/FormikStyledSelectAsync';
import { FormikTextField } from '../../Components/FormikFields/FormikTextField';
import { ArrowDownIcon, ArrowRightIcon } from '../../Components/Icons';
import { isDosageTimesInstruction } from '../../Contracts/PatientMedication.Response';
import { requiredField } from '../../Medications.en';
import { ValidDateFormats } from '../../Shared/DateUtilities';
import { referenceToKey, referenceToString } from '../../Shared/SharedModels';
import { capitalizeFirstLetter } from '../../Shared/StringUtilities';
import { primaryGray } from '../../Styles/Colours';
import { EditDosageTimeSelectorForm } from './EditDosageTimeSelector';
import styles from './EditDosageTimesInstructionForm.module.css';
export var dosageTimesInstructionSchema = yup.object();
export var strictDosageTimesInstructionSchema = function (routeRequired, adjustMedication) {
    return yup.object({
        type: yup.mixed(),
        textInstructions: yup.string(),
        instructions: yup.string().required('Instructions are required'),
        routeOfAdministration: yup
            .object()
            .nullable()
            .test({
            name: 'Route of administration is required',
            message: 'Route of administration is required',
            test: function (value) {
                if (routeRequired)
                    return !!value;
                return true;
            }
        }),
        frequency: yup.object().nullable(),
        times: yup.array(yup.string()),
        startsOn: yup
            .string()
            .required(requiredField)
            .test({
            name: 'start-on-date-check',
            message: 'Start date cannot be in the past',
            test: function (value) {
                if (adjustMedication)
                    return true;
                var valueMoment = moment(value, ValidDateFormats.NodeDateTimeTimezone);
                return moment().endOf('day').isSameOrBefore(valueMoment, 'day');
            }
        })
            .test({
            name: 'start-on-date-not-greater-than-expiry',
            message: function () {
                var maxExpiryDate = getExpiryDate().format('DD-MMM-YYYY');
                return "Start date cannot be same or after chart expiry (" + maxExpiryDate + ")";
            },
            test: function (value) {
                var maxExpiryDate = getExpiryDate();
                var valueMoment = moment(value, ValidDateFormats.NodeDateTimeTimezone);
                return valueMoment.isBefore(maxExpiryDate, 'day');
            }
        })
    });
};
function getExpiryDate() {
    var remainingDays = moment().daysInMonth() - moment().date();
    var newDate = moment().add(remainingDays, 'days');
    var maxExpiryDate = newDate.add(3, 'months').endOf('month');
    return maxExpiryDate;
}
function SentenceItem(props) {
    if (props.readOnly && !props.value)
        return null;
    return (React.createElement(React.Fragment, null,
        !props.firstItem ? ' ' : false,
        props.prefix ? props.prefix + " " : false,
        React.createElement("span", { className: styles.item }, props.value ? props.value : props.default)));
}
function SentenceBuild(props) {
    var _a, _b;
    var instruction = props.form.values[props.fieldPath];
    if (!isDosageTimesInstruction(instruction))
        return null;
    var instructions = instruction.instructions;
    var routeOfAdministration = instruction.routeOfAdministration;
    var times = instruction.times;
    var startsOn = moment(instruction.startsOn, ValidDateFormats.NodeDateTimeTimezone);
    var showStartsOn = false;
    if (startsOn && startsOn.isValid()) {
        showStartsOn = moment().endOf('day').isBefore(startsOn, 'day');
    }
    if (props.useDefaultValue && props.defaultValue)
        return (React.createElement("div", { "data-test-id": 'sentence-builder', className: styles.sentenceBuilder }, capitalizeFirstLetter(props.defaultValue)));
    return (React.createElement("div", { "data-test-id": 'sentence-builder', className: styles.sentenceBuilder },
        React.createElement(SentenceItem, { firstItem: true, value: instructions === '' ? '' : capitalizeFirstLetter(instructions), default: '[Administration dosage instructions]' }),
        React.createElement(SentenceItem, { prefix: 'via', readOnly: props.readOnly, value: (_b = (_a = routeOfAdministration === null || routeOfAdministration === void 0 ? void 0 : routeOfAdministration.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '', default: '[route of administration]' }),
        React.createElement(SentenceItem, { prefix: 'at', readOnly: props.readOnly, value: times.length ? times.join(', ') : '', default: '[time]' }),
        showStartsOn && (React.createElement(SentenceItem, { prefix: 'starting on', readOnly: props.readOnly, value: moment(startsOn).format(ValidDateFormats.Edit), default: '[date]' }))));
}
export var InstructionSource;
(function (InstructionSource) {
    InstructionSource["DosageInstruction"] = "DosageInstruction";
    InstructionSource["OrderInstruction"] = "OrderInstruction";
})(InstructionSource || (InstructionSource = {}));
export var EditDosageTimesInstructionForm = function (props) {
    var _a;
    var _b, _c;
    var _d = __read(useState(true), 2), showSavedSentence = _d[0], setShowSavedSentence = _d[1];
    var fieldTouched = function () {
        if (showSavedSentence)
            setShowSavedSentence(false);
    };
    var GetStartsOnDateValue = function (e) {
        if (showSavedSentence)
            setShowSavedSentence(false);
        props.getStartOnDate(e);
    };
    var currentTimes = (_c = (_b = props.initialDosageInstruction) === null || _b === void 0 ? void 0 : _b.times) !== null && _c !== void 0 ? _c : [];
    var _e = __read(useState(currentTimes.length > 0), 2), showDoseTimes = _e[0], setShowDoseTimes = _e[1];
    function toggleShowDoseTimes() {
        setShowDoseTimes(!showDoseTimes);
    }
    var readOnly = !props.isAdjustMedication && props.readOnly;
    return (React.createElement("div", { className: styles.dosageTimesInstructionsForm },
        React.createElement(SentenceBuild, { form: props.form, fieldPath: props.fieldPath, defaultValue: props.initialDosageInstruction.textInstructions, useDefaultValue: showSavedSentence, readOnly: readOnly || props.disableSentenceBuilder }),
        React.createElement("div", { className: styles.instructions },
            React.createElement(FormikTextField, { readOnly: readOnly || props.disableInstructions, name: props.fieldPath + ".instructions", label: props.label, multiline: true, maxLength: 250, required: props.required, testId: 'test-DosageTimesInstructionFormStructure', onChange: fieldTouched })),
        !props.hideFrequency && (React.createElement("div", { className: styles.frequency },
            React.createElement(FormikStyledSelectAsync, { readOnly: readOnly || props.disableFrequency, name: props.fieldPath + ".frequency", label: 'Frequency', maxResults: 5, itemToString: referenceToString, itemToMenuKey: referenceToKey, testId: 'selection-input-frequency', fetch: searchFrequency, isClearable: true, onSelection: fieldTouched }))),
        React.createElement("div", { className: styles.routeOfAdministration },
            React.createElement(FormikStyledSelectAsync, { readOnly: readOnly || props.disableRouteOfAdministration, name: props.fieldPath + ".routeOfAdministration", label: 'Route of administration', required: props.requiredRouteOfAdministration, maxResults: 5, itemToString: referenceToString, itemToMenuKey: referenceToKey, testId: 'selection-input-route-of-administration', fetch: searchRouteOfAdministration, isClearable: true, onSelection: fieldTouched })),
        React.createElement(FormikDatePicker, { format: ValidDateFormats.NodeDateTimeTimezone, name: props.fieldPath + ".startsOn", label: 'Starts on', testId: 'starts-on-field', fillContainer: true, readOnly: readOnly || props.disableStartsOn, required: props.required, onChange: GetStartsOnDateValue }),
        React.createElement("div", { className: styles.dosageTimeSelector },
            props.enableHidingOfDosageTimes && (React.createElement(FormControlLabel, { className: classNames(styles.showDoseTimesExpander, (_a = {},
                    _a[styles.showDoseTimesSpacer] = props.enableHidingOfDosageTimes && showDoseTimes,
                    _a)), onClick: toggleShowDoseTimes, label: 'Show dose times', labelPlacement: 'end', control: showDoseTimes ? React.createElement(ArrowDownIcon, { color: primaryGray }) : React.createElement(ArrowRightIcon, { color: primaryGray }) })),
            (!props.enableHidingOfDosageTimes || showDoseTimes) && (React.createElement(EditDosageTimeSelectorForm, { form: props.form, fieldPath: props.fieldPath, readOnly: readOnly || props.disableTimePicker, onChange: fieldTouched })))));
};
